import type {ReactElement} from 'react'
import {Icon} from 'pepsico-ds'
import type {DocsArticleVideo} from '@/services/cms/docsCmsService'
import {formatVideoTime} from './formatVideoTime'

interface PageDocsVideoThumbnailProps {
  video: DocsArticleVideo
  isDark: boolean
  onPlay: () => void
}

const DEFAULT_VIDEO_TITLE = 'Video walkthrough'

/** Poster with a play button and a caption shown at the top of an article; clicking it opens the video modal. */
const PageDocsVideoThumbnail = ({video, isDark, onPlay}: PageDocsVideoThumbnailProps): ReactElement => {
  const title =
    video.title?.trim() !== undefined && video.title.trim() !== '' ? video.title.trim() : DEFAULT_VIDEO_TITLE
  const aspectRatio =
    video.width !== null && video.height !== null && video.height > 0 ? video.width / video.height : 16 / 9

  return (
    <div className="mb-5">
      <button
        type="button"
        aria-label={`Play video: ${title}`}
        data-testid="page-docs-video-thumbnail"
        onClick={onPlay}
        className={`group relative block w-full cursor-pointer overflow-hidden rounded-lg border p-0 shadow-sm transition-shadow duration-200 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 ${
          isDark ? 'border-[var(--color-border-default)]' : 'border-slate-200'
        }`}
        style={{
          aspectRatio,
          backgroundColor: '#0f172a',
          backgroundImage: video.blurUpThumb ? `url(${video.blurUpThumb})` : undefined,
          backgroundSize: 'cover',
        }}
      >
        {video.thumbnailUrl && (
          <img
            src={video.thumbnailUrl}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        )}
        <span className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/30" />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-[#004b93] shadow-lg transition-transform duration-200 group-hover:scale-110">
            <Icon icon="play_arrow" size="large" style={{color: 'currentColor'}} />
          </span>
        </span>
        {video.durationSeconds !== null && (
          <span className="absolute right-2 bottom-2 rounded bg-black/75 px-1.5 py-0.5 text-xs font-medium text-white tabular-nums">
            {formatVideoTime(video.durationSeconds)}
          </span>
        )}
      </button>
      <p
        data-testid="page-docs-video-caption"
        className={`m-0 mt-2 text-[13px] leading-5 font-medium ${
          isDark ? 'text-[var(--color-text-secondary)]' : 'text-slate-600'
        }`}
      >
        {title}
      </p>
    </div>
  )
}

export default PageDocsVideoThumbnail
